/* Per-lead SMS thread + tap-to-call panel.
 *
 * Renders on the LeadReport page. Polls Twilio's per-lead thread endpoint
 * every 8s so inbound replies show up without a page refresh. Shipping with
 * three empty states (no Twilio connection / no lead phone / no messages),
 * a basic delivery-status badge per bubble, and a `tel:` link for tap-to-call
 * (no Twilio voice involved — uses the wholesaler's own dialer + caller ID).
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  MessageSquare, Send, Loader2, Phone, AlertTriangle, CheckCheck,
  ChevronDown,
} from "lucide-react";
import CallButton from "@/components/CallButton";

const POLL_MS = 8000;

const fmtPhone = (raw) => {
  if (!raw) return "";
  const d = raw.replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  if (d.length === 10) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return raw;
};

const STATUS_LABEL = {
  queued: "Queued",
  sending: "Sending",
  sent: "Sent",
  delivered: "Delivered",
  undelivered: "Undelivered",
  failed: "Failed",
  received: "Received",
};

const STATUS_TONE = {
  delivered: "text-emerald-600",
  sent: "text-[var(--ink-2)]",
  queued: "text-[var(--ink-3)]",
  sending: "text-[var(--ink-3)]",
  received: "text-[var(--ink-2)]",
  undelivered: "text-rose-600",
  failed: "text-rose-600",
};

function MessageBubble({ msg }) {
  const isOut = msg.direction === "outbound";
  const ts = msg.created_at ? new Date(msg.created_at).toLocaleString() : "";
  return (
    <div
      className={`flex ${isOut ? "justify-end" : "justify-start"}`}
      data-testid={`sms-msg-${msg.id}`}
    >
      <div className={`max-w-[78%] ${isOut ? "items-end" : "items-start"} flex flex-col`}>
        <div
          className={`px-3 py-2 rounded-2xl text-sm leading-snug ${
            isOut
              ? "bg-[var(--primary)] text-white rounded-br-sm"
              : "bg-slate-100 text-[var(--ink)] rounded-bl-sm"
          }`}
        >
          {msg.body}
        </div>
        <div className={`text-[10px] mt-1 flex items-center gap-1 ${STATUS_TONE[msg.status] || "text-[var(--ink-3)]"}`}>
          <span className="font-mono">{ts}</span>
          {isOut && (
            <>
              <span>·</span>
              {(msg.status === "delivered") && <CheckCheck size={11} />}
              <span className="font-semibold">{STATUS_LABEL[msg.status] || msg.status}</span>
              {msg.error_message && (
                <span title={msg.error_message} className="inline-flex items-center gap-0.5">
                  <AlertTriangle size={10} /> Error
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SmsThread({ lead }) {
  const [senders, setSenders] = useState(null);     // {options, default_from_number, has_managed, has_byoa}
  const [selectedFrom, setSelectedFrom] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [dncStatus, setDncStatus] = useState(null);   // {is_dnc, ...} | null
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  // Connected = user has at least one valid sender (Managed OR BYOA).
  const connected = !!(senders?.options?.length);

  // Load the user's available 'from' senders. The /senders endpoint covers
  // both iSpeedToClose-managed numbers AND a connected BYOA Twilio account.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/twilio/senders");
        if (!alive) return;
        setSenders(data);
        setSelectedFrom(data?.default_from_number || "");
      } catch {
        if (alive) setSenders({ options: [], has_managed: false, has_byoa: false });
      }
    })();
    return () => { alive = false; };
  }, []);

  // Initial fetch + polling. Only poll when Twilio is connected & we have a
  // phone + lead id — otherwise the surrounding empty-state UI renders instead
  // and `loading` never reaches the DOM.
  useEffect(() => {
    if (!connected || !lead?.seller_phone || !lead?.id) return;
    let active = true;
    const fetchThread = async (showSpinner) => {
      if (showSpinner) setLoading(true);
      try {
        const { data } = await api.get(`/twilio/threads/${lead.id}`);
        if (active) setMessages(data?.items || []);
      } catch {
        // Silent: probably no connection or lead phone isn't set yet.
      } finally {
        if (active && showSpinner) setLoading(false);
      }
    };
    fetchThread(true);
    pollRef.current = setInterval(() => fetchThread(false), POLL_MS);
    return () => {
      active = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [connected, lead?.seller_phone, lead?.id]);

  // Auto-scroll to bottom when a new message arrives.
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  // DNC pre-flight: when the lead's phone changes, query our cached
  // skip-trace data to see if the National DNC registry has flagged this
  // number. We show a warning banner BEFORE the user types so they're
  // never surprised mid-message.
  useEffect(() => {
    if (!connected || !lead?.seller_phone) {
      setDncStatus(null);
      return;
    }
    let alive = true;
    api.get("/twilio/dnc-check", { params: { to: lead.seller_phone } })
      .then((r) => { if (alive) setDncStatus(r.data); })
      .catch(() => { if (alive) setDncStatus(null); });
    return () => { alive = false; };
  }, [connected, lead?.seller_phone]);

  const send = async (acknowledgeConsent = false) => {
    const body = draft.trim();
    if (!body) return;
    if (!lead?.seller_phone) {
      toast.error("Add a phone number to this lead first.");
      return;
    }
    setSending(true);
    try {
      const { data: sent } = await api.post("/twilio/send", {
        lead_id: lead.id,
        to: lead.seller_phone,
        body,
        from_number: selectedFrom || undefined,
        acknowledge_consent: acknowledgeConsent,
      });
      setDraft("");
      setMessages((m) => [...m, sent]);
    } catch (e) {
      // 451 → DNC block. Surface a clear confirmation prompt instead of a
      // generic error toast — the user needs to explicitly attest to consent.
      if (e?.response?.status === 451) {
        const detail = e.response.data?.detail || {};
        const confirmed = window.confirm(
          "⚠️ This number is on the National Do-Not-Call registry.\n\n" +
          "Sending without prior express written consent could trigger a " +
          "$500–$1,500 TCPA penalty PER MESSAGE.\n\n" +
          "Only proceed if you have signed consent on file from the seller " +
          "(e.g. an opted-in capture form, contract, or written authorization).\n\n" +
          "Send anyway and log my consent attestation?"
        );
        if (confirmed) {
          setSending(false);
          return send(true);  // retry with override
        }
      } else {
        toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't send. Check your Twilio connection.");
      }
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      send();
    }
  };

  // ── Empty states ────────────────────────────────────────────────────
  if (!senders) {
    return (
      <div className="card mb-5 print:hidden text-center py-6 text-[var(--ink-2)]">
        <Loader2 size={18} className="animate-spin mx-auto mb-2" /> Loading SMS…
      </div>
    );
  }

  if (!connected) {
    return (
      <div className="card mb-5 print:hidden" data-testid="sms-thread-disconnected">
        <div className="flex items-start gap-3">
          <MessageSquare className="text-[var(--ink-2)] mt-0.5" size={18} />
          <div className="flex-1">
            <div className="font-semibold text-sm">No SMS sender connected</div>
            <p className="text-xs text-[var(--ink-2)] mt-1">
              Buy a $5/mo iSpeedToClose-managed number or connect your own Twilio in Settings to text leads directly from this page.
            </p>
          </div>
          <Link to="/v3/settings" className="btn-outline text-xs whitespace-nowrap" data-testid="sms-connect-cta">
            Get a number
          </Link>
        </div>
      </div>
    );
  }

  if (!lead?.seller_phone) {
    return (
      <div className="card mb-5 print:hidden" data-testid="sms-thread-nophone">
        <div className="flex items-start gap-3">
          <MessageSquare className="text-[var(--ink-2)] mt-0.5" size={18} />
          <div className="flex-1">
            <div className="font-semibold text-sm">No phone number on this lead</div>
            <p className="text-xs text-[var(--ink-2)] mt-1">
              Add a phone number in the lead&apos;s edit modal to start an SMS thread.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card mb-5 print:hidden" data-testid="sms-thread-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="flex items-center gap-2 group"
          data-testid="sms-thread-toggle"
        >
          <MessageSquare size={16} className="text-[var(--primary)]" />
          <span className="font-display font-bold text-base">SMS thread</span>
          <span className="text-xs text-[var(--ink-3)]">· {fmtPhone(lead.seller_phone)}</span>
          <ChevronDown
            size={14}
            className={`text-[var(--ink-3)] transition-transform ${collapsed ? "-rotate-90" : ""}`}
          />
        </button>
        <a
          href={`tel:${lead.seller_phone}`}
          className="btn-outline inline-flex items-center gap-1.5 text-xs"
          data-testid="sms-thread-call-link"
        >
          <Phone size={12} /> Call
        </a>
        <CallButton phone={lead.seller_phone} variant="full" label="Recorded call" />
      </div>

      {!collapsed && (
        <>
          {/* Thread */}
          <div
            ref={scrollRef}
            className="border border-[var(--line)] rounded-lg bg-white p-3 mb-3 max-h-[420px] min-h-[140px] overflow-y-auto space-y-2"
            data-testid="sms-thread-scroll"
          >
            {loading ? (
              <div className="text-center text-[var(--ink-3)] text-sm py-8">
                <Loader2 size={16} className="animate-spin mx-auto mb-2" /> Loading…
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center text-[var(--ink-3)] text-sm py-8" data-testid="sms-thread-empty">
                No messages yet — send your first SMS below.
              </div>
            ) : (
              messages.map((m) => <MessageBubble key={m.id} msg={m} />)
            )}
          </div>

          {/* DNC warning banner — shown when we have positive evidence the
              number is on the National Do-Not-Call registry. */}
          {dncStatus?.is_dnc && (
            <div
              className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs flex items-start gap-2"
              data-testid="sms-thread-dnc-warning"
            >
              <AlertTriangle size={14} className="text-red-700 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-red-900">DNC-listed number</div>
                <div className="text-red-800">
                  This phone is on the National Do-Not-Call registry. Sending without prior express written consent risks a $500–$1,500 TCPA penalty per message.
                </div>
              </div>
            </div>
          )}

          {/* Sender picker — only when 2+ options. Single-sender users skip this. */}
          {senders.options.length > 1 && (
            <div className="mb-2 flex items-center gap-2 text-xs">
              <span className="text-[var(--ink-3)]">From:</span>
              <select
                className="input py-1 text-xs flex-1 max-w-[280px]"
                value={selectedFrom}
                onChange={(e) => setSelectedFrom(e.target.value)}
                data-testid="sms-thread-sender-picker"
              >
                {senders.options.map((opt) => (
                  <option key={opt.from_number} value={opt.from_number}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Composer */}
          <div className="flex gap-2 items-end">
            <textarea
              className="input flex-1 min-h-[60px] resize-none"
              placeholder="Type a message… (⌘/Ctrl + Enter to send)"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKey}
              maxLength={1600}
              data-testid="sms-thread-input"
            />
            <button
              type="button"
              className={dncStatus?.is_dnc ? "btn-outline self-stretch border-red-300 text-red-700" : "btn-money self-stretch"}
              onClick={() => send()}
              disabled={sending || !draft.trim()}
              data-testid="sms-thread-send-btn"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {sending ? "Sending" : dncStatus?.is_dnc ? "Send (DNC)" : "Send"}
            </button>
          </div>
          <div className="text-[10px] text-[var(--ink-3)] mt-1 text-right tabular-nums">
            {draft.length}/1600
          </div>
        </>
      )}
    </div>
  );
}
